import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { NavigationSidebarStaff } from '@components/app/navigation/NavigationSidebarStaff'
import { AppShell, Button, Transition } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { useElementSize, useHotkeys, useMediaQuery } from '@mantine/hooks'
import { DefaultHeader } from '@components/app/header/AppHeader'
import { ActivitySidebar } from '@components/app/ActivitySidebar'
import { NavigationSidebarParent } from '@components/app/navigation/NavigationSidebarParent'
import { useLayoutControlsStore } from '@stores/useLayoutControlsStore'
import { useHostMode, useSetViewMode, useViewMode } from '@stores/usePortalModeStore'
import { NavigationSidebarStudent } from '@components/app/navigation/NavigationSidebarStudent'
import { SettingsModal } from '@features/settings/components/SettingsModal'
import { NewEventRouteModal } from '@features/calendar/components/NewEventRouteModal'
import { CalendarDayViewRouteModal } from '@features/calendar/components/CalendarDayViewRouteModal'
import { RouteErrorFallback } from '@components/errors/RouteErrorFallback'
import { useEffect, useRef } from 'react'
import { useStaffOverrideStore } from '@stores/useStaffOverrideStore'
import { allStaffOidsQueryOptions } from '@features/send-teams-message/query'
import { useRoleOverrideStore } from '@stores/useRoleOverrideStore'
import { AppSpotlight } from '@features/spotlight/components/AppSpotlight'
import { BannerStack } from '@features/banner/components/BannerStack'
import { activeBannersQueryOptions } from '@features/banner/queries'
import { RealtimeBridge } from '@features/realtime/RealtimeBridge'
import { Footer } from '@components/app/footer/Footer'
import { useMePermissionsSuspense } from '@features/rbac/queries'
import { meSessionQueryOptions, useMeSession } from '@features/me/queries'
import { useStaffImpersonationPopoverStore } from '@stores/useStaffImpersonationPopoverStore'
import { hasPermission } from '@features/rbac/permissions'
import { useNavigationColor } from '@hooks/useNavigationColor'
import { StudentModal } from '@features/portal-tabs/components/StudentModal'
import { PortalTabsHeader } from '@features/portal-tabs/components/PortalTabsHeader'
import { PortalTabsRouteSync } from '@features/portal-tabs/components/PortalTabsRouteSync'
import { RoomViewModal } from '@features/rooms/components/RoomViewModal'
import { userQueryOptions } from '@features/user-account/queries'
import { PortalModeSelect } from '@components/app/navigation/PortalModeSelect'
import { StudentContractGate } from '@features/student-contracts/components/StudentContractGate'
import { StudentInductionGate } from '@features/it-induction/components/StudentInductionGate'
import { CourseOverviewModal } from '@features/course-overview/components/CourseOverviewModal'
import { notificationsRealtimeClient } from '@services/realtime/notificationsSignalR'
import { playNotificationTone } from '@features/notifications/notificationSound'
import { useNotificationSettingsStore } from '@features/notifications/store'

export const Route = createFileRoute('/_layout')({
    component: RouteComponent,
    beforeLoad: async ({ context }) => {
        if (context.auth.status !== 'authed') {
            throw redirect({ to: '/sign-in', search: { returnUrl: window.location.pathname + window.location.search + window.location.hash } })
        }
        // if (context.auth.hostMode === 'parent') {
        //     const { status } = await context.auth.ensureAuth()
        
        //     if (status !== 'authed') {
        //         throw redirect({ to: '/sign-in', search: { returnUrl: window.location.pathname + window.location.search + window.location.hash } })
        //     }
        // }
        return null;
    },
    loader: async ({ context }) => {
        context.queryClient.ensureQueryData(activeBannersQueryOptions())
        context.queryClient.ensureQueryData(meSessionQueryOptions())
        context.queryClient.ensureQueryData(userQueryOptions())
        // Prefetch the connection string to determine if we're connected to CIS or not, so we can show this in the footer immediately
        if (context.auth.hostMode === 'staff') {
            context.queryClient.ensureQueryData(allStaffOidsQueryOptions())
        }
    },
    errorComponent: ({ error, reset }) => (
        <RouteErrorFallback
            error={error}
            onRetry={reset}
            title="Uh oh... something went very wrong"
            description="An unexpected error occurred and crashed the app."
            actions={(
                <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => {
                        window.localStorage.clear()
                        window.location.reload()
                    }}
                >
                    Clear local data and reload
                </Button>
            )}
        />
    )
})

function RouteComponent() {
    const mobileNavbarWidth = 'min(calc(100vw - 2.5rem), 22rem)'
    const mobileAsideWidth = 'min(calc(100vw - 2.5rem), 22rem)'
    const defaultHeaderHeight = 52

    const { mobileOpenedNav, desktopOpenedNav, mobileOpenedAside, desktopOpenedAside, setState } = useLayoutControlsStore();

    const { ref: headerRef, height: measuredHeaderHeight } = useElementSize();
    const isMobile = useMediaQuery('(max-width: 48em)')

    const hostMode = useHostMode()
    const viewMode = useViewMode()
    const staffOverride = useStaffOverrideStore((state) => state.override)
    const staffOverrideType = useStaffOverrideStore((state) => state.overrideType)
    const setViewMode = useSetViewMode()
    const queryClient = useQueryClient()
    const previousStaffOverride = useRef(staffOverride)
    const requestOpenStaffImpersonation = useStaffImpersonationPopoverStore((state) => state.requestOpen)
    const { color: navigationColor, variables: navigationColorVariables } = useNavigationColor()
    const { data: permissions } = useMePermissionsSuspense()
    const canUseThreeCx = permissions.some((permission) => permission.toLowerCase() === '3cx-integration.use')

    useEffect(() => {
        if (!isMobile) return
        setState({
            mobileOpenedNav: false,
            mobileOpenedAside: false,
        })
    }, [isMobile, setState])

    useEffect(() => {
        if (viewMode !== 'student') return
        setState({ desktopOpenedNav: false })
    }, [setState, viewMode])

    useEffect(() => {
        // The loader has already fetched initial data. Invalidate only on an
        // identity change, including when impersonation is cleared.
        if (previousStaffOverride.current === staffOverride) return
        previousStaffOverride.current = staffOverride
        void queryClient.invalidateQueries()
    }, [queryClient, staffOverride])

    useEffect(() => {
        if (hostMode !== 'staff') return
        void notificationsRealtimeClient
            .connect(() => {
                if (useNotificationSettingsStore.getState().soundEnabled) {
                    playNotificationTone()
                }
                void queryClient.refetchQueries({ queryKey: ['notifications'], type: 'active' })
            })
            .catch((error) => console.error('Notifications realtime connection failed', error))
        return () => { void notificationsRealtimeClient.disconnect() }
    }, [hostMode, queryClient, staffOverride])

    useEffect(() => {
        if (staffOverride && staffOverrideType && staffOverrideType !== 'staff' && viewMode !== staffOverrideType) setViewMode(staffOverrideType)
    }, [setViewMode, staffOverride, staffOverrideType, viewMode])

    const headerHeight = Math.max(measuredHeaderHeight, defaultHeaderHeight)

    const openStaffImpersonationPopover = () => {
        if (!hasPermission('staff.impersonate')) {
            return
        }

        requestOpenStaffImpersonation()
    }

    useHotkeys([
        ['mod + I', openStaffImpersonationPopover]
    ])

    return (
        <>
            <RealtimeBridge />
            {hostMode === 'staff' ? <StaffRoleSync /> : null}
            <StudentInductionGate>
            <StudentContractGate>
            <AppShell
                header={{ height: headerHeight }}
                footer={{ height: '1rem' }}
                withBorder
                navbar={{
                    width: { base: mobileNavbarWidth, sm: 300 },
                    breakpoint: 'sm',
                    collapsed: { mobile: !mobileOpenedNav, desktop: !desktopOpenedNav },
                }}
                aside={{
                    width: { base: mobileAsideWidth, sm: 320 },
                    breakpoint: 'sm',
                    collapsed: { mobile: !mobileOpenedAside, desktop: !desktopOpenedAside },
                }}
                //layout='alt'
                style={{ backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-9))' }}
                styles={{
                    header: {
                        borderBottom: 'none',
                    },
                }}
            >
                <Transition mounted={isMobile && mobileOpenedNav} transition="fade" duration={200} timingFunction="ease">
                    {(transitionStyles) => (
                        <div
                            aria-hidden="true"
                            onClick={() => setState({ mobileOpenedNav: false })}
                            style={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 100,
                                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                                backdropFilter: 'blur(7px)',
                                WebkitBackdropFilter: 'blur(7px)',
                                ...transitionStyles,
                            }}
                        />
                    )}
                </Transition>
                <Transition mounted={isMobile && mobileOpenedAside} transition="fade" duration={200} timingFunction="ease">
                    {(transitionStyles) => (
                        <div
                            aria-hidden="true"
                            onClick={() => setState({ mobileOpenedAside: false })}
                            style={{
                                position: 'fixed',
                                inset: 0,
                                zIndex: 100,
                                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                                backdropFilter: 'blur(7px)',
                                WebkitBackdropFilter: 'blur(7px)',
                                ...transitionStyles,
                            }}
                        />
                    )}
                </Transition>

                <AppShell.Header>
                    <div ref={headerRef}>
                        <BannerStack />
                        <DefaultHeader />
                    </div>
                </AppShell.Header>

                <AppShell.Navbar style={{ ...navigationColorVariables, borderRight: undefined, maxWidth: mobileNavbarWidth, borderRightColor: `light-dark(var(--mantine-color-${navigationColor}-8), var(--mantine-color-default-border))` }}>
                    <div
                        className="shrink-0 p-2 sm:hidden"
                        style={{ backgroundColor: `light-dark(var(--mantine-color-${navigationColor}-9), var(--mantine-color-dark-9))` }}
                    >
                        <PortalModeSelect />
                    </div>
                    <div className="min-h-0 flex-1">
                        {viewMode === 'staff' && <NavigationSidebarStaff />}
                        {viewMode === 'student' && <NavigationSidebarStudent />}
                        {viewMode === 'parent' && <NavigationSidebarParent />}
                    </div>
                </AppShell.Navbar>

                <AppShell.Main
                    id='app-shell-main'
                    className={'min-w-0 w-full'}
                    style={{
                        height: 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px))',
                        maxHeight: 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px))',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <PortalTabsRouteSync />
                    <PortalTabsHeader />
                    <div className="h-full min-h-0 flex-1">
                        <Outlet />
                    </div>
                    <StudentModal />
                </AppShell.Main>

                <AppShell.Aside style={{ backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-9))', maxWidth: mobileAsideWidth }}>
                    <ActivitySidebar canUseCalls={hostMode === 'staff' && viewMode === 'staff' && canUseThreeCx} />
                </AppShell.Aside>

                <AppShell.Footer style={{ borderTop: 'none' }}>
                    <Footer />
                </AppShell.Footer>

                <AppSpotlight />

                <SettingsModal />
                <NewEventRouteModal />
                <CalendarDayViewRouteModal />
                <RoomViewModal />
                <CourseOverviewModal />

            </AppShell>
            </StudentContractGate>
            </StudentInductionGate>
        </>
    )
}

function StaffRoleSync() {
    const setActualRoles = useRoleOverrideStore((state) => state.setActualRoles)
    const { data: session } = useMeSession()

    useEffect(() => {
        const effectiveRoleCodes = session?.effectiveStaffProfile?.roles.map((role) => role.code) ?? []
        setActualRoles(effectiveRoleCodes)
    }, [session?.effectiveStaffProfile?.roles, setActualRoles])

    return null
}
